import { Button, cn } from '@lemonenergy/lemonpie-3'
import { Info } from '@lemonenergy/lemonpie-icons'
import { Link, useLocation, useNavigate, useNavigation } from '@remix-run/react'

import CircularLoading from './CircularLoading'
import { CentralizedContainer } from './Loader'

type PageVariant = 'primary' | 'neutral'

interface ErrorPageProps {
  label?: string
  onClick?: () => void
  href?: string
  text: string
  /**
   * Especifies the page's colors variation
   */
  variant?: PageVariant
  className?: string
}

const ErrorPage = ({
  label = 'Tentar novamente',
  onClick,
  href,
  text,
  variant = 'neutral',
  className,
  ...props
}: ErrorPageProps) => {
  const bgColorClass = variant === 'primary' ? 'bg-expressive-strong' : ''
  const textColorClass =
    variant === 'primary' ? 'fg-inverted-main' : 'fg-disabled'

  const navigate = useNavigate()
  const navigation = useNavigation()
  const location = useLocation()

  const defaultHandleClick = () => {
    if (href) return null
    navigate('.')
  }

  const isReloading =
    location.pathname === navigation.location?.pathname &&
    navigation.state === 'loading'

  if (isReloading) {
    return (
      <CentralizedContainer>
        <CircularLoading />
      </CentralizedContainer>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col justify-center items-center flex-1 h-full',
        bgColorClass,
        textColorClass,
        'errorPage',
        className,
      )}
      {...props}
    >
      <Info className="size-largest mb-[1.6875rem]" />
      <h5 className="typography-copy max-w-[275px] text-center">{text}</h5>

      {label && (
        <Button
          data-testid="error-page-button"
          /**
           * The Button should have a visible background,
           * therefore it should have the alternate color
           * option from the page's background
           */
          variant={variant === 'primary' ? 'tertiary' : 'primary'}
          inline
          onClick={onClick ?? defaultHandleClick}
          className="min-w-[9rem] mt-larger"
          asChild={Boolean(href)}
        >
          {href ? <Link to={href}>{label}</Link> : label}
        </Button>
      )}
    </div>
  )
}

export default ErrorPage
